<template>
  <div>
    <app-spinner v-if="loading" />
    <app-cells v-else-if="failed">
      <span>Unable to load payment</span>
      <app-button @click.native="fetchPayment">Retry</app-button>
    </app-cells>
    <template v-else-if="payment">
      <app-cells position="between" gap="10px 12px">
        <app-cells :indent="false" gap="10px 12px">
          <h1 class="title">{{ payment.number || `Payment ${payment.id}` }}</h1>
          <funding-status :status="payment.status" />
        </app-cells>
        <funding-invoice-export :key="invoiceKey" :payment="payment" :payment-busy="busy" />
      </app-cells>
      <app-cells v-if="payment.status === 'draft'">
        <app-button :disabled="busy" @click.native="$refs.fundingModal.openDraft(payment)">Edit draft</app-button>
        <app-button :disabled="busy || !canConfirm" @click.native="command = 'confirm'">Confirm funding</app-button>
        <app-button theme="error" :disabled="busy" @click.native="command = 'cancel'">Cancel draft</app-button>
      </app-cells>
      <app-cells v-if="command">
        <span>{{ command === 'confirm' ? 'Confirm ' + formatAmount(payment.funding_amount, payment.currency) + ' for ' + payment.lines.length + ' tours?' : 'Cancel this draft and release all reservations?' }}</span>
        <app-button :disabled="savingCommand" :aria-busy="savingCommand" @click.native="runCommand">{{ command === 'confirm' ? 'Confirm payment' : 'Cancel payment' }}</app-button>
        <app-button theme="dark" :disabled="savingCommand" @click.native="command = ''">Back</app-button>
      </app-cells>
      <app-cells v-if="isAltezzaBatch(payment) && !payment.relationship">Historical payment: a verified funding direction must be assigned before adding funding or confirming.</app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div v-if="payment.relationship" class="info-cards__row"><div class="info-cards__row-item info-cards__row-item--full"><span>Direction:</span><span>{{ relationshipName(payment.relationship) }}{{ payment.relationship.active ? '' : ' (inactive)' }}</span></div></div>
          <div class="info-cards__row"><div class="info-cards__row-item info-cards__row-item--full"><span>Recipient:</span><span>{{ recipientName(payment.recipient) }}</span></div></div>
          <div class="info-cards__row"><div class="info-cards__row-item info-cards__row-item--full"><span>Created:</span><span>{{ formatDate(payment.created_at) }}</span></div></div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row"><div class="info-cards__row-item info-cards__row-item--full"><span>Funding amount:</span><span>{{ formatAmount(payment.funding_amount, payment.currency) }}</span></div></div>
        </div>
      </div>
      <funding-tours-table :items="payment.lines" detail />
      <funding-modal ref="fundingModal" :key="payment.id" :recipient="batchRecipient(payment)" :relationship="payment.relationship ? payment.relationship.slug : ''" :direction="payment.relationship || null" @saved="applyPayment" @editing="editing = $event" />
    </template>
  </div>
</template>

<script>
import { getTransferPricingFundingBatch, postTransferPricingFundingConfirm, postTransferPricingFundingCancel } from '@/http'
import FundingToursTable from './ToursTable'
import FundingModal from './FundingModal'
import FundingStatus from './Status'
import FundingInvoiceExport from './FundingInvoiceExport'
import { batchRecipient, formatAmount, formatDate, isAltezzaBatch, recipientName, relationshipName } from './funding'

export default {
  name: 'TransferPricingFundingDetail',
  components: { FundingModal, FundingToursTable, FundingStatus, FundingInvoiceExport },
  data() {
    return {
      payment: null,
      loading: false,
      failed: false,
      requestId: 0,
      savingCommand: false,
      command: '',
      editing: false,
    }
  },
  computed: {
    busy() { return this.loading || this.savingCommand || this.editing || !!this.command },
    canConfirm() {
      return !!this.payment && this.payment.lines.length > 0 && (!!this.payment.relationship && this.payment.relationship.active)
    },
    invoiceKey() {
      return this.payment ? `${this.payment.id}-${this.payment.latest_invoice_export ? this.payment.latest_invoice_export.id : 'none'}` : ''
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler: 'fetchPayment',
    },
  },
  beforeDestroy() {
    this.requestId++
  },
  methods: {
    formatAmount,
    formatDate,
    recipientName,
    relationshipName,
    batchRecipient,
    isAltezzaBatch,
    applyPayment(payment) {
      if (String(payment.id) !== String(this.$route.params.id)) return
      this.payment = payment
    },
    async runCommand() {
      if (!this.payment || this.payment.status !== 'draft' || !this.command || this.savingCommand || this.editing) return
      if (this.command === 'confirm' && !this.canConfirm) return
      const requestId = this.requestId
      const action = this.command
      this.savingCommand = true
      try {
        const execute = action === 'confirm' ? postTransferPricingFundingConfirm : postTransferPricingFundingCancel
        const { data } = await execute(this.payment.id)
        if (requestId !== this.requestId) return
        this.applyPayment(data)
        this.command = ''
        this.$notify({ type: 'success', title: action === 'confirm' ? 'Funding confirmed' : 'Draft cancelled; reservations released' })
      } catch (error) {
        // A rejected action leaves the draft and its reservations unchanged.
      } finally {
        if (requestId === this.requestId) this.savingCommand = false
      }
    },
    async fetchPayment() {
      const requestId = ++this.requestId
      this.loading = true
      this.failed = false
      this.savingCommand = false
      this.command = ''
      this.editing = false
      try {
        const { data } = await getTransferPricingFundingBatch(this.$route.params.id)
        if (requestId !== this.requestId) return
        this.payment = data
      } catch (error) {
        if (requestId === this.requestId) this.failed = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
  },
}
</script>
